/**
 * Lead-magnet structured content — v1 schema types, defensive parsing, and the
 * rich-field sanitizer (Phase 2 of the structured-content plan; the contract is
 * CC's docs/LEAD-MAGNET-STRUCTURED-CONTENT-HANDOFF.md, approved 2026-09-08).
 *
 * The worker mirrors `landing_content` / `thankyou_content` from CC's feed as
 * opaque JSON; THIS module is where EE decides whether that JSON is renderable.
 * The parsers return null on anything malformed or missing a required field —
 * the page then falls back to the legacy landing_html/thankyou_html render, so
 * bad content degrades to yesterday's page, never to a broken one. Unknown
 * top-level sections are ignored entirely (the contract says never "best
 * effort"); a `schema` other than 1 also returns null until EE explicitly
 * supports it.
 *
 * `attribution` is REQUIRED and the landing parser refuses content without it:
 * it carries the IP posture ("original work from TSB, not a reproduction") —
 * dropping it on render breaks the IP firewall, not just the layout. Failing
 * the whole parse (→ legacy fallback, which carries its own attribution line)
 * is the only safe degradation.
 */

// --- v1 schema types (mirror CC's src/lead-magnet-content.ts) ---------------

export interface MagnetLandingContent {
  schema: 1;
  hero: {
    tag?: string;          // optional pill above the hook
    hook: string;          // plain text
    subhook: string;       // plain text
    cta_line: string;      // plain text — heading of EE's capture form card
    bullets?: string[];    // optional plain-text bullets inside the form card
    form_note: string;     // plain text
    reassurance?: string;  // optional plain text ("Why free? …")
  };
  proof?: {
    intro: string;         // RICH
    stats: { value: string; label: string }[];
  };
  whats_inside: {
    eyebrow: string;
    heading: string;
    items: string[];       // RICH
  };
  testimonials?: {
    eyebrow: string;
    heading: string;
    quotes: { text: string; attribution: string }[]; // text is RICH
    outro?: string;        // RICH
  };
  who_its_for: {
    eyebrow: string;
    heading: string;
    paragraphs: string[];  // RICH
  };
  secondary_cta?: {
    label: string;
    note?: string;
  };
  attribution: string;     // REQUIRED — must render (IP posture)
}

export interface MagnetThankyouContent {
  schema: 1;
  headline: string;        // plain text
  confirmation: string[];  // RICH
  next_step: {
    heading: string;
    paragraphs: string[];  // RICH
    /** Pairs with the definition's funnel_target_url; null url → no button. */
    cta_label: string;
  };
}

// --- Sanitizer ---------------------------------------------------------------

/** Sanitize a rich content field to the contract's allowlist — <strong>, <em>,
 *  <br> and nothing else. Applied at render to EVERY rich field regardless of
 *  what arrives (the contract's own rule). Uses an inert <template> element:
 *  nothing executes during parse, disallowed elements are unwrapped to their
 *  text, and all text is re-escaped on the way out — the returned string is
 *  safe for dangerouslySetInnerHTML by construction. */
export function sanitizeRich(input: string): string {
  const tpl = document.createElement('template');
  tpl.innerHTML = input;

  const esc = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const serialize = (node: Node): string => {
    let out = '';
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += esc(child.textContent ?? '');
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = (child as Element).tagName.toLowerCase();
        if (tag === 'strong' || tag === 'em') {
          out += `<${tag}>${serialize(child)}</${tag}>`; // attributes dropped
        } else if (tag === 'br') {
          out += '<br/>';
        } else {
          out += serialize(child); // disallowed element → unwrap to its text
        }
      }
      // comments/other node types are dropped entirely
    }
    return out;
  };

  return serialize(tpl.content);
}

// --- Defensive parsers -------------------------------------------------------

function isObj(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === 'object' && !Array.isArray(v);
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}
function optStr(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}
function strArray(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const out = v.filter((x): x is string => typeof x === 'string' && !!x.trim());
  return out.length ? out : null;
}

/** Parse + validate a landing_content payload. Null on anything that can't be
 *  rendered faithfully — the caller falls back to the legacy HTML path. */
export function parseMagnetLandingContent(raw: unknown): MagnetLandingContent | null {
  if (!isObj(raw) || raw.schema !== 1) return null;

  const heroRaw = raw.hero;
  if (!isObj(heroRaw)) return null;
  const hook = str(heroRaw.hook);
  const subhook = str(heroRaw.subhook);
  const ctaLine = str(heroRaw.cta_line);
  const formNote = str(heroRaw.form_note);
  if (!hook || !subhook || !ctaLine || !formNote) return null;

  const wiRaw = raw.whats_inside;
  const wiItems = isObj(wiRaw) ? strArray(wiRaw.items) : null;
  const wiEyebrow = isObj(wiRaw) ? str(wiRaw.eyebrow) : null;
  const wiHeading = isObj(wiRaw) ? str(wiRaw.heading) : null;
  if (!wiItems || !wiEyebrow || !wiHeading) return null;

  const wifRaw = raw.who_its_for;
  const wifParas = isObj(wifRaw) ? strArray(wifRaw.paragraphs) : null;
  const wifEyebrow = isObj(wifRaw) ? str(wifRaw.eyebrow) : null;
  const wifHeading = isObj(wifRaw) ? str(wifRaw.heading) : null;
  if (!wifParas || !wifEyebrow || !wifHeading) return null;

  const attribution = str(raw.attribution);
  if (!attribution) return null; // required — IP posture; refuse to render without it

  const content: MagnetLandingContent = {
    schema: 1,
    hero: {
      tag: optStr(heroRaw.tag),
      hook, subhook, cta_line: ctaLine, form_note: formNote,
      bullets: strArray(heroRaw.bullets) ?? undefined,
      reassurance: optStr(heroRaw.reassurance),
    },
    whats_inside: { eyebrow: wiEyebrow, heading: wiHeading, items: wiItems },
    who_its_for: { eyebrow: wifEyebrow, heading: wifHeading, paragraphs: wifParas },
    attribution,
  };

  // Optional sections: a present-but-malformed section is dropped (the page
  // renders without it) rather than failing the whole parse.
  if (isObj(raw.proof)) {
    const intro = str(raw.proof.intro);
    const statsRaw = raw.proof.stats;
    const stats = Array.isArray(statsRaw)
      ? statsRaw
          .map((s) => (isObj(s) ? { value: str(s.value), label: str(s.label) } : null))
          .filter((s): s is { value: string; label: string } => !!s?.value && !!s?.label)
      : [];
    if (intro && stats.length) content.proof = { intro, stats };
  }

  if (isObj(raw.testimonials)) {
    const t = raw.testimonials;
    const eyebrow = str(t.eyebrow);
    const heading = str(t.heading);
    const quotes = Array.isArray(t.quotes)
      ? t.quotes
          .map((q) => (isObj(q) ? { text: str(q.text), attribution: str(q.attribution) } : null))
          .filter((q): q is { text: string; attribution: string } => !!q?.text && !!q?.attribution)
      : [];
    if (eyebrow && heading && quotes.length) {
      content.testimonials = { eyebrow, heading, quotes, outro: optStr(t.outro) };
    }
  }

  if (isObj(raw.secondary_cta)) {
    const label = str(raw.secondary_cta.label);
    if (label) content.secondary_cta = { label, note: optStr(raw.secondary_cta.note) };
  }

  return content;
}

/** Parse + validate a thankyou_content payload — same posture as the landing. */
export function parseMagnetThankyouContent(raw: unknown): MagnetThankyouContent | null {
  if (!isObj(raw) || raw.schema !== 1) return null;

  const headline = str(raw.headline);
  const confirmation = strArray(raw.confirmation);
  const nsRaw = raw.next_step;
  if (!headline || !confirmation || !isObj(nsRaw)) return null;

  const heading = str(nsRaw.heading);
  const paragraphs = strArray(nsRaw.paragraphs);
  const ctaLabel = str(nsRaw.cta_label);
  if (!heading || !paragraphs || !ctaLabel) return null;

  return {
    schema: 1,
    headline,
    confirmation,
    next_step: { heading, paragraphs, cta_label: ctaLabel },
  };
}
